import { useEffect, useState } from 'react';

import { getPartialRecognition, getPartialTask } from 'services/dashboard.service';

type PartialDraftsEligibility = {
  /** `canRegisterRealizacao(userInfo)`. */
  canRegister: boolean;
  /** `canRecognizeNow(...)`. */
  canRecognize: boolean;
};

/**
 * Leitura de um rascunho gated pela elegibilidade: quem não pode usar o rascunho não o lê, e o
 * resultado já nasce `false` para o sinal da fila de modais não ficar esperando (DCE-16, DCE-17).
 * O evento `reloadEvent` relê 1 vez (DCE-42).
 */
function useGatedDraft(enabled: boolean, reloadEvent: string, readDraft: () => Promise<boolean>) {
  const [hasDraft, setHasDraft] = useState<boolean>();

  useEffect(() => {
    if (!enabled) {
      setHasDraft(false);
      return;
    }

    // Pendente até a leitura chegar: a fila de modais não pode tomar o `false` de antes (quando
    // ainda não era elegível) como decisão.
    setHasDraft(undefined);
    let active = true;

    function load() {
      readDraft()
        .then(found => {
          if (active) setHasDraft(found);
        })
        .catch(error => console.error(`Erro ao carregar rascunho (${reloadEvent}):`, error));
    }

    load();
    document.addEventListener(reloadEvent, load, false);

    return () => {
      active = false;
      document.removeEventListener(reloadEvent, load);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  return hasDraft;
}

async function readPartialTask() {
  const partialTask = await getPartialTask();
  return !!partialTask?.team;
}

async function readPartialRecognition() {
  const partialRecognition = await getPartialRecognition();
  return (
    !!partialRecognition?.target ||
    !!partialRecognition?.behavior ||
    !!partialRecognition?.coin ||
    !!partialRecognition?.message
  );
}

/** Rascunhos de realização (`get_partial_task`) e de reconhecimento (`get_partial_recognition`). */
export function usePartialDrafts({ canRegister, canRecognize }: PartialDraftsEligibility) {
  const hasPartialTask = useGatedDraft(canRegister, 'reload:partialTask', readPartialTask);
  const hasPartialRecognition = useGatedDraft(canRecognize, 'reload:partialRecognition', readPartialRecognition);

  return { hasPartialTask, hasPartialRecognition };
}
